import {
  BarChart3,
  CircleHelp,
  ClipboardList,
  FileBadge,
  Package,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type NavigationVariant = "rail" | "list";

type NavigationItem = {
  label: string;
  href: string;
  icon: LucideIcon;
};

type NavigationGroup = {
  key: string;
  label: string;
  items: NavigationItem[];
};

function routeIsActive(href: string, pathname: string) {
  const productAnalysis = /^\/admin\/products\/.+\/usage\/?$/.test(pathname);

  if (href === "/admin/usage") {
    return (
      pathname === "/admin" || pathname === "/admin/usage" || productAnalysis
    );
  }
  if (href === "/admin/products" && productAnalysis) return false;
  return pathname.startsWith(href);
}

export function AdminNavigation({
  variant,
  roles = [],
  onNavigate,
}: {
  variant: NavigationVariant;
  roles?: string[];
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const groups: NavigationGroup[] = [
    {
      key: "workspace",
      label: t("nav.workspace", "Workspace"),
      items: [
        {
          label: t("nav.dashboard", "Dashboard"),
          href: "/admin/usage",
          icon: BarChart3,
        },
        {
          label: t("nav.licenses", "Licenses"),
          href: "/admin/licenses",
          icon: FileBadge,
        },
      ],
    },
    {
      key: "records",
      label: t("nav.records", "Records"),
      items: [
        {
          label: t("nav.products", "Products"),
          href: "/admin/products",
          icon: Package,
        },
        {
          label: t("nav.customers", "Customers"),
          href: "/admin/customers",
          icon: Users,
        },
      ],
    },
    {
      key: "control",
      label: t("nav.control", "Control"),
      items: [
        {
          label: t("nav.auditLog", "Audit log"),
          href: "/admin/audit",
          icon: ClipboardList,
        },
        ...(roles.includes("Security Admin")
          ? [
              { label: t("nav.apiActivity"), href: "/admin/api-activity", icon: ClipboardList },
              {
                label: t("nav.adminAccounts", "Admin Accounts"),
                href: "/admin/accounts",
                icon: ShieldCheck,
              },
            ]
          : []),
        {
          label: t("nav.gettingStarted", "Help & guides"),
          href: "/admin/help",
          icon: CircleHelp,
        },
      ],
    },
  ];

  if (variant === "rail") {
    return (
      <TooltipProvider delay={300} closeDelay={80}>
        <nav
          className="flex flex-col items-center gap-3"
          aria-label={t("nav.primary", "Primary navigation")}
        >
          {groups.map((group, groupIndex) => (
            <div
              className={cn(
                "flex flex-col items-center gap-1",
                groupIndex > 0 && "border-t border-line pt-3",
              )}
              key={group.key}
            >
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = routeIsActive(item.href, location.pathname);
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger>
                      <Link
                        to={item.href}
                        onClick={onNavigate}
                        className={cn(
                          "group relative flex size-11 items-center justify-center rounded-full border border-transparent text-muted transition-colors hover:border-line hover:bg-panel hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus",
                          active &&
                            "border-line bg-panel-raised text-ink hover:bg-panel-raised hover:text-ink",
                        )}
                        aria-current={active ? "page" : undefined}
                        aria-label={item.label}
                      >
                        <Icon className="size-[18px]" aria-hidden="true" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </nav>
      </TooltipProvider>
    );
  }

  return (
    <nav className="space-y-5" aria-label={t("nav.primary", "Primary navigation")}>
      {groups.map((group) => (
        <div key={group.key}>
          <p className="mb-2 px-3 font-mono text-[10px] font-normal uppercase tracking-[0.14em] text-muted">
            {group.label}
          </p>
          <div className="space-y-1">
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = routeIsActive(item.href, location.pathname);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onNavigate}
                  className={cn(
                    "group flex min-h-10 pointer-coarse:min-h-11 items-center gap-3 rounded-full border border-transparent px-3 text-sm text-muted transition-colors hover:bg-panel hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus",
                    active && "border-line bg-panel-raised text-ink hover:bg-panel-raised hover:text-ink",
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon
                    className={cn(
                      "size-4",
                      active ? "text-ink" : "text-muted group-hover:text-ink",
                    )}
                    aria-hidden="true"
                  />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
